import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './PaymentForm.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const RAZORPAY_KEY_ID = process.env.REACT_APP_RAZORPAY_KEY_ID;

const PaymentForm = ({ userData }) => {
  const { memberId } = useParams();
  const navigate = useNavigate();

  const [memberData, setMemberData] = useState(null);
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Find the selected member from userData
    if (userData && userData.members) {
      const member = userData.members.find(m => m.Mem_Id === parseInt(memberId));
      if (member) {
        setMemberData(member);

        // Set default amount if it's a fixed payment type
        if (member.Mem_FixedOrUnfixed?.toLowerCase() === 'fixed' && member.Member_Amount) {
          setAmount(member.Member_Amount.toString());
        }
      }
    }
  }, [userData, memberId]);

  // Load Razorpay script dynamically
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleAmountChange = (e) => {
    setAmount(e.target.value);
  };

  const validateForm = () => {
    if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount');
      return false;
    }
    return true;
  };

  const handlePayment = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError('');

    try {
      // Create order on the server
      const orderResponse = await axios.post(`${API_URL}/create-order`, {
        amount: parseFloat(amount),
        memberId: memberData.Mem_Id,
      });

      if (!orderResponse.data.success) {
        throw new Error(orderResponse.data.message || 'Failed to create order');
      }

      const order = orderResponse.data.order;

      // Initialize Razorpay payment
      const options = {
        key: RAZORPAY_KEY_ID,
        amount: order.amount,
        currency: order.currency,
        name: "JWL Payment System",
        description: `Payment for ${memberData.Mem_Name}`,
        order_id: order.id,
        handler: function(response) {
          verifyPayment(response, order);
        },
        prefill: {
          name: memberData.Mem_Name,
          contact: memberData.Mem_Phone,
        },
        theme: {
          color: "#3399cc"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      setError(err.message || 'Payment initialization failed');
      console.error('Payment error:', err);
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (response, order) => {
    try {
      setLoading(true);

      const verifyResponse = await axios.post(`${API_URL}/verify-payment`, {
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_order_id: response.razorpay_order_id,
        razorpay_signature: response.razorpay_signature,
        memberId: memberData.Mem_Id,
        memberName: memberData.Mem_Name,
        memberCode: memberData.Mem_Code,
        memberPhone: memberData.Mem_Phone,
        companyName: memberData.Company_Name,
        fixedOrUnfixed: memberData.Mem_FixedOrUnfixed,
        amount: parseFloat(amount)
      });

      if (verifyResponse.data.success) {
        navigate('/payment-success', {
          state: {
            paymentId: response.razorpay_payment_id,
            amount,
            memberName: memberData.Mem_Name
          }
        });
      } else {
        setError('Payment verification failed');
      }
    } catch (err) {
      setError('Payment verification failed');
      console.error('Verification error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!memberData) {
    return <div className="loading-container">Loading member data...</div>;
  }

  return (
    <div className="payment-container">
      <div className="payment-card">
        <h2>Make Payment</h2>

        <div className="member-details">
          <p><strong>Scheme ID:</strong> {memberData.Mem_MemberId}</p>

        </div>

        <form onSubmit={handlePayment} className="payment-form">
          <div className="form-group">
            <label htmlFor="amount">Payment Amount (₹)</label>
            <input
              type="number"
              id="amount"
              value={amount}
              onChange={handleAmountChange}
              placeholder="Enter amount"
              readOnly={memberData.Mem_FixedOrUnfixed?.toLowerCase() === 'fixed'}
              className={memberData.Mem_FixedOrUnfixed?.toLowerCase() === 'fixed' ? 'readonly' : ''}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <div className="button-group">
            <button
              type="button"
              className="cancel-button"
              onClick={() => navigate('/member-dashboard')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pay-button"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaymentForm;


